import { motion } from "framer-motion";
import usePreferStaticUI from "@/lib/usePreferStaticUI";
import Reveal from "@/components/common/Reveal";
import HoverGlow from "@/components/motion/HoverGlow";
import TiltCard from "@/components/motion/TiltCard";

/**
 * Animated grid for text cards or editorial image tiles.
 *
 * items: { title?, body?, image?, testId?, children? }
 * variant: "card" (default) | "image"
 */
export const VisualTileGrid = ({
  items = [],
  variant = "card",
  testId,
  className = "",
  staggerDelay = 0.05,
  columns = "sm:grid-cols-2 lg:grid-cols-3",
}) => {
  const reduced = usePreferStaticUI();

  return (
    <div
      className={`grid ${columns} gap-4 ${variant === "image" ? "gap-5" : ""} ${className}`}
      data-testid={testId}
    >
      {items.map((item, i) => (
        <Reveal key={item.key || item.title || item.image || i} delay={i * staggerDelay}>
          {variant === "image" ? (
            <TiltCard maxTilt={5} scale={1.015}>
              <motion.div
                className={`wit-bento-cell p-0 overflow-hidden ${
                  i % 3 === 1 ? "lg:mt-6" : ""
                }`}
                whileHover={reduced ? undefined : { y: -4 }}
                transition={{ duration: 0.35, ease: [0.22, 0.61, 0.36, 1] }}
                data-testid={item.testId || `tile-${i}`}
              >
                <div className="aspect-[4/5]">
                  <img
                    src={item.image}
                    alt={item.alt || ""}
                    aria-hidden={item.alt ? undefined : true}
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
              </motion.div>
            </TiltCard>
          ) : (
            <HoverGlow className="h-full">
              <article
                className="wit-card h-full group"
                data-testid={item.testId || `tile-${i}`}
              >
                {item.children || (
                  <>
                    {item.title && (
                      <h3 className="wit-heading-card">{item.title}</h3>
                    )}
                    {item.body && (
                      <p className="text-sm text-warmGrey leading-relaxed">{item.body}</p>
                    )}
                  </>
                )}
              </article>
            </HoverGlow>
          )}
        </Reveal>
      ))}
    </div>
  );
};

export default VisualTileGrid;
